import { Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import CTable from "../../../components/Table/table";
import {
  getAllExams,
  getManualExams,
} from "../../../modules/admin-modules/exams/Api";
import { getTopStudentsRequest } from "../../../modules/admin-modules/students/Actions";

const TopStudents = () => {
  const dispatch = useDispatch();
  const [selectedExams, setSelectedExams] = useState([]);
  const [combinedExams, setCombinedExams] = useState([]);
  const [topN, setTopN] = useState(5);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);

  const topStudents = useSelector((state) => state.students.topStudents);
  const isLoading = useSelector((state) => state.students.isLoading);

  useEffect(() => {
    let cancelled = false;
    const loadExams = async () => {
      try {
        const [allRes, manualRes] = await Promise.all([
          getAllExams(),
          getManualExams(),
        ]);
        if (cancelled) return;
        const all = allRes?.data?.data || [];
        const manual = manualRes?.data?.data || [];
        const manualIds = new Set(manual.map((exam) => exam?.id).filter((id) => id != null));
        const byId = new Map();
        [...all, ...manual].forEach((exam) => {
          if (exam?.id == null) return;
          byId.set(exam.id, {
            ...exam,
            isManual: manualIds.has(exam.id),
          });
        });
        const getCreatedTime = (exam) => {
          const time = Date.parse(exam?.created_at);
          return Number.isNaN(time) ? null : time;
        };
        setCombinedExams(
          Array.from(byId.values()).sort((a, b) => {
            const timeA = getCreatedTime(a);
            const timeB = getCreatedTime(b);
            if (timeA != null && timeB != null && timeA !== timeB) {
              return timeA - timeB;
            }
            if (timeA != null && timeB == null) return -1;
            if (timeA == null && timeB != null) return 1;
            return (a.id || 0) - (b.id || 0);
          })
        );
      } catch (error) {
        // keep empty options on failure
      }
    };
    loadExams();
    return () => {
      cancelled = true;
    };
  }, []);

  const examsOptions = combinedExams?.map((data) => ({
    value: data.id,
    label: data.isManual ? `${data.name} (سنتر)` : data.name,
  }));

  const examNameById = useMemo(() => {
    const map = {};
    combinedExams?.forEach((exam) => {
      map[String(exam.id)] = exam.isManual ? `${exam.name} (سنتر)` : exam.name;
    });
    selectedExams?.forEach((exam) => {
      map[String(exam.value)] = exam.label;
    });
    return map;
  }, [combinedExams, selectedExams]);

  const colourStyles = {
    control: (styles) => ({
      ...styles,
      width: "100%",
      minWidth: "220px",
      background: "#f5f5f5",
      color: "#690000",
      borderRadius: "16px",
    }),
    option: (styles) => ({ ...styles, color: "#690000" }),
  };

  const handleFetch = () => {
    if (!selectedExams?.length) return;
    const formData = {
      exams: selectedExams.map((exam) => exam.value),
    };
    if (topN) {
      formData.top_n = Number(topN);
    }
    dispatch(getTopStudentsRequest(formData));
  };

  const examColumns = useMemo(() => {
    const examIds = new Set();
    selectedExams?.forEach((exam) => examIds.add(String(exam.value)));
    topStudents?.forEach((student) => {
      Object.keys(student?.exam_scores || {}).forEach((id) => examIds.add(String(id)));
    });
    return Array.from(examIds).map((examId) => ({
      Header: examNameById[examId] || `Exam ${examId}`,
      accessor: `exam_${examId}`,
    }));
  }, [selectedExams, topStudents, examNameById]);

  const columns = [
    { Header: "#", accessor: "index" },
    { Header: "اسم الطالب", accessor: "name" },
    { Header: "كود الطالب", accessor: "username" },
    { Header: "رقم الطالب", accessor: "phone" },
    { Header: "رقم ولي الأمر", accessor: "parent_phone" },
    ...examColumns,
    { Header: "إجمالي الإجابات الصحيحة", accessor: "total_right_answers" },
  ];

  const Data = topStudents?.map((data, index) => {
    const row = {
      index: `#${index + 1}`,
      id: data?.student_id,
      name: data?.name,
      username: data?.username,
      phone: data?.phone,
      parent_phone: data?.parent_phone,
      total_right_answers: data?.total_right_answers,
    };
    examColumns.forEach((col) => {
      const examId = col.accessor.replace("exam_", "");
      row[col.accessor] = data?.exam_scores?.[examId] ?? data?.exam_scores?.[Number(examId)] ?? "-";
    });
    return row;
  });

  const headers = [
    { label: "name", key: "name" },
    { label: "code", key: "username" },
    { label: "phone", key: "phone" },
    { label: "parent_phone", key: "parent_phone" },
    ...examColumns.map((col) => ({ label: col.Header, key: col.accessor })),
    { label: "total_right_answers", key: "total_right_answers" },
  ];

  return (
    <Layout>
      <div style={{ minHeight: "400px" }}>
        <Flex
          w="100%"
          my="30px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
          zIndex="100"
          flexWrap="wrap"
          gap={3}
        >
          <Text fontSize="24px" fontWeight="600" color="main_1">
            الطلاب المتمييزين
          </Text>
          <Flex
            w={{ base: "100%", md: "70%" }}
            alignItems="center"
            gap={2}
            flexWrap="wrap"
            justifyContent="flex-end"
          >
            <Box flex="1" minW="220px">
              <Select
                options={examsOptions}
                isMulti={true}
                value={selectedExams}
                onChange={(data) => setSelectedExams(data || [])}
                styles={colourStyles}
                placeholder="اختر الامتحانات"
              />
            </Box>
            <Input
              type="number"
              min={1}
              value={topN}
              onChange={(e) => setTopN(e.target.value)}
              placeholder="Top N"
              w="100px"
              bg="#f5f5f5"
              borderRadius="16px"
            />
            <Button
              colorScheme="red"
              borderRadius="16px"
              onClick={handleFetch}
              isDisabled={!selectedExams?.length}
              isLoading={isLoading}
            >
              عرض
            </Button>
            <Box border="1px solid gray" borderRadius="8px" padding="10px">
              <CSVLink
                headers={headers}
                data={Data?.length != 0 ? Data : []}
                filename={"top-students.csv"}
                className="btn btn-primary"
                target="_blank"
              >
                Download
              </CSVLink>
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        <CTable
          selectedData={() => {}}
          Columns={columns}
          Data={Data}
          path={"openModel"}
          Actions
          Title={``}
          subTitle=""
          btnTitle=""
          placeHolder=""
          noSearchBar={true}
          noFilter={true}
          footerBtnTitle
          filterList
          addButton
          addButtonTitle=""
          setPage={() => {}}
          setPerPage={setPerPage}
          currentpage={pageNumber}
          setPageNumber={setPageNumber}
          noSecondSearchBar={true}
          isLoading={isLoading}
          perPage={size}
          totalPage={Math.ceil((Data?.length || 0) / size) || 1}
        />
      </div>
    </Layout>
  );
};

export default TopStudents;
